import { useUser } from "@/context/UserContext";
import type { Course } from "@/services/courses/types/course.types";
import { fetchPdcExternalCourseList } from "@/services/pdcExternal/courses";
import { ensurePdcEmbedApiKey } from "@/utils/pdcEmbed";
import {
  canFetchPdcExternalCourseList,
  isPdcExternalCourseListEnabled,
} from "@/utils/pdc-external-course-list";
import { useQuery } from "react-query";

/**
 * React Query for `GET /external/courses` (sg-pdc-api). Key includes `userId` so cache never
 * mixes users or keeps a stale 404 across logins.
 */
export function usePdcExternalCoursesQuery(options?: { enabled?: boolean }) {
  const { userInfo, isLoadingUser } = useUser();
  const usePdc = isPdcExternalCourseListEnabled();
  const parentEnabled = options?.enabled !== false;
  const enabled = Boolean(
    usePdc &&
      parentEnabled &&
      !isLoadingUser &&
      canFetchPdcExternalCourseList(userInfo)
  );

  const userId = userInfo?.id;

  return useQuery<Course[]>(
    ["get-courses-pdc", userId],
    async () => {
      if (userId == null) {
        throw new Error("Not authenticated");
      }
      ensurePdcEmbedApiKey(userId);
      return fetchPdcExternalCourseList(userId);
    },
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    }
  );
}
